import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { IonicModule } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../services/auth.service';
import { UserService } from '../../services/user.service';
import { AnalyticsService } from '../../services/gtag.service';
import { DisplayService } from '../../services/display.service';
import { User } from '../../entities/user.model';

@Component({
  selector: 'app-setup',
  standalone: true,
  imports: [IonicModule, CommonModule, FormsModule, TranslateModule],
  templateUrl: './setup.page.html',
  styleUrls: ['./setup.page.scss'],
})
export class SetupPage implements OnInit {
  textSize: 1 | 2 | 3 | 4 | 5 = 3; // Default to medium size
  darkMode: boolean = false; // Default to light mode
  private currentUser: User | null = null;
  private nativeLanguage = '';
  private targetLanguage = '';
  private selectedLevel = 1;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private authService: AuthService,
    private userService: UserService,
    private analyticsService: AnalyticsService,
    private displayService: DisplayService
  ) {
    // Get the passed state from navigation
    const navigation = this.router.getCurrentNavigation();
    if (navigation?.extras?.state) {
      this.currentUser = navigation.extras.state['user'];

      // Handle language data (may be passed with or without user)
      const languageData = navigation.extras.state['languageData'];
      if (languageData) {
        // Store language data as component properties for validation
        this.nativeLanguage = languageData.nativeLanguage;
        this.targetLanguage = languageData.targetLanguage;
        this.selectedLevel = languageData.selectedLevel;

        // If user exists, apply language settings
        if (this.currentUser) {
          this.currentUser.nativeLanguage = languageData.nativeLanguage;
          this.currentUser.targetLanguage = languageData.targetLanguage;
          this.currentUser.level = { [languageData.targetLanguage]: languageData.selectedLevel };
        }
      }
    }
  }

  ngOnInit() {
    document.title = 'Setup Preferences';

    // Use the user data passed from level page, or get current user
    this.currentUser = this.currentUser || this.authService.currentUserValue;

    if (this.currentUser) {
      // Load existing preferences if available
      this.textSize = this.currentUser.textSize || 3;
      this.darkMode = this.currentUser.darkMode || false;

      // Set the app language to user's native language
      const languageToUse = this.currentUser.nativeLanguage || this.nativeLanguage;
      if (languageToUse) {
        this.translate.use(languageToUse);
      }
    } else if (this.nativeLanguage) {
      // We have language data but no user - this is fine, we'll create a user later
      this.textSize = 3; // Default
      this.darkMode = false; // Default
      this.translate.use(this.nativeLanguage);
    } else {
      // Redirect to beginning if no user and no language data
      this.router.navigate(['/beginning']);
      return;
    }

    // Apply initial theme
    this.applyTheme();
  }

  private applyTheme() {
    // Apply theme changes immediately for preview
    this.displayService.setDarkMode(this.darkMode);
    this.displayService.setTextSize(this.textSize);
  }

  // Called when text size changes
  onTextSizeChange() {
    this.displayService.setTextSize(this.textSize);
  }

  // Called when theme changes
  onThemeChange() {
    this.displayService.setDarkMode(this.darkMode);
  }

  // Get button text based on user tour completion status
  getButtonText(): string {
    if (this.currentUser?.completedTour) {
      return this.translate.instant('SETUP.APPLY');
    }
    return this.translate.instant('SETUP.START_LEARNING');
  }

  // Check if user has completed tour
  hasCompletedTour(): boolean {
    return this.currentUser?.completedTour || false;
  }

  async savePreferences() {
    try {
      // If no user exists, create an anonymous user
      if (!this.currentUser) {
        console.log('No user found, creating anonymous user...');
        const firebaseUser = await this.authService.signInAnonymously(this.nativeLanguage);

        if (firebaseUser) {
          // Wait for user to be created in Firestore
          await new Promise(resolve => setTimeout(resolve, 1000));
          this.currentUser = this.authService.currentUserValue;

          // Set user ID for analytics tracking
          if (this.currentUser) {
            await this.analyticsService.setUserId(this.currentUser.uid);
            this.analyticsService.trackEvent('user_created', 'anonymous', 'setup_preferences');
          }
        }
      }

      if (!this.currentUser) {
        console.error('Failed to create or retrieve user');
        return;
      }

      // Update user with all preferences and required fields
      this.currentUser.textSize = this.textSize;
      this.currentUser.darkMode = this.darkMode;

      // Ensure all required fields are set
      if (!this.currentUser.nativeLanguage || !this.currentUser.targetLanguage || !this.currentUser.level) {
        // Try to use the stored language data if user object doesn't have it
        if (this.nativeLanguage && this.targetLanguage) {
          this.currentUser.nativeLanguage = this.nativeLanguage;
          this.currentUser.targetLanguage = this.targetLanguage;
          this.currentUser.level = { [this.targetLanguage]: this.selectedLevel };
        } else {
          console.error('Missing required user fields');
          return;
        }
      }

      // Apply theme immediately
      this.applyTheme();

      // Save changes to Firestore
      await this.userService.applyChanges(this.currentUser);

      // Track analytics
      await this.analyticsService.trackEvent('preferences_saved', 'setup', 'user_preferences');

      // Navigate based on whether user has completed tour
      if (this.currentUser.completedTour) {
        // User has completed tour, go back to settings or news
        this.router.navigate(['/settings']);
      } else {
        // New user, go to welcome to start tour
        this.router.navigate(['/welcome']);
      }
    } catch (error) {
      console.error('Error saving preferences:', error);
    }
  }

  goBack() {
    // If user has completed tour, go back to settings
    if (this.currentUser?.completedTour) {
      this.router.navigate(['/settings']);
      return;
    }

    // Otherwise, handle normal flow
    const native = this.currentUser?.nativeLanguage || this.nativeLanguage;
    const target = this.currentUser?.targetLanguage || this.targetLanguage;

    if (native && target) {
      this.router.navigate(['/level'], {
        queryParams: {
          native: native,
          target: target
        }
      });
    } else {
      this.router.navigate(['/beginning']);
    }
  }
}
